import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, FormControl, InputLabel, Select, MenuItem, Box,
    Switch, FormControlLabel, Typography, Alert, IconButton, Grid
} from '@mui/material';
import { skillService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Close as CloseIcon } from '@mui/icons-material';

const SkillFormModal = ({ open, onClose, skill, categories, onSuccess }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        skill_name: '',
        description: '',
        category_id: '',
        status: true
    });

    useEffect(() => {
        if (skill) {
            setFormData({
                skill_name: skill.skill_name || '',
                description: skill.description || '',
                category_id: skill.category_id || '',
                status: skill.status === 1
            });
        } else {
            setFormData({
                skill_name: '',
                description: '',
                category_id: '',
                status: true
            });
        }
        setError('');
    }, [skill, open]);

    const handleChange = (event) => {
        const { name, value, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'status' ? checked : value
        }));
        if (error) {
            setError('');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!formData.skill_name.trim()) {
            showToast('Skill name is required', 'error');
            return;
        }
        
        if (!formData.category_id) {
            showToast('Category is required', 'error');
            return;
        }

        setLoading(true);
        try {
            const submitData = {
                ...formData,
                status: formData.status ? 1 : 0
            };

            if (skill) {
                await skillService.updateSkill(skill.id, submitData);
                showToast('Skill updated successfully', 'success');
            } else {
                await skillService.createSkill(submitData);
                showToast('Skill created successfully', 'success');
            }
            
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error saving skill:', error);
            const message = error.response?.data?.message || 'Error saving skill';
            showToast(message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setError('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">{skill ? 'Edit Skill' : 'Add New Skill'}</Typography>
                        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
            <form onSubmit={handleSubmit} style={{
                display: 'flex',
                flexDirection: 'column',
                
                minHeight: 0,
            }}> 
                <DialogContent 
                    dividers
                    sx={{                    
                        minHeight: 0,
                        overflowY: 'auto',
                        pt: 2,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                name="skill_name"
                                label="Skill Name"
                                value={formData.skill_name}
                                onChange={handleChange}
                                required
                                fullWidth
                                helperText={error && (error.includes('Skill name') || error.includes('already exists')) ? error : ''}
                            />
                        </Grid>                        
                        <Grid item xs={12}>
                            <FormControl fullWidth required size="small">
                                <InputLabel>Category</InputLabel>
                                <Select
                                    name="category_id"
                                    value={formData.category_id}
                                    onChange={handleChange}
                                    label="Category"
                                >
                                    {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.category_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="description"
                                label="Description"
                                value={formData.description}
                                onChange={handleChange}
                                multiline
                                rows={3}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="status"
                                        checked={formData.status}
                                        onChange={handleChange}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : (skill ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SkillFormModal;